import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CharityCard = ({ charity, viewMode = 'grid' }) => {
  const [isBookmarked, setIsBookmarked] = useState(charity?.isBookmarked || false);
  const navigate = useNavigate();

  const handleBookmark = (e) => {
    e?.stopPropagation();
    setIsBookmarked(!isBookmarked);
  };

  const handleDonate = (e) => {
    e?.stopPropagation();
    console.log('Quick donate to:', charity?.name);
  };

  const handleCardClick = () => {
    navigate('/charity-profile', { state: { charity } });
  };

  const TrustBadges = () => (
    <div className="flex items-center space-x-1">
      {charity?.isVerified && (
        <div className="flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-full">
          <Icon name="Shield" size={12} />
          <span className="text-xs font-medium">Verified</span>
        </div>
      )}
      {charity?.transparencyScore >= 90 && (
        <div className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-full">
          <Icon name="Eye" size={12} />
          <span className="text-xs font-medium">{charity?.transparencyScore}%</span>
        </div>
      )}
      {charity?.efficiencyRating >= 4.5 && (
        <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-full">
          <Icon name="Target" size={12} />
          <span className="text-xs font-medium">{charity?.efficiencyRating}/5</span>
        </div>
      )}
    </div>
  );

  const ImpactMetrics = () => (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <p className="text-muted-foreground">Total Raised</p>
        <p className="font-semibold text-foreground">${charity?.totalRaised?.toLocaleString()}</p>
      </div>
      <div>
        <p className="text-muted-foreground">People Helped</p>
        <p className="font-semibold text-foreground">{charity?.peopleHelped?.toLocaleString()}</p>
      </div>
    </div>
  );

  if (viewMode === 'list') {
    return (
      <div 
        className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-2 transition-gentle cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex items-start space-x-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden">
              <Image
                src={charity?.logo}
                alt={`${charity?.name} logo`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-semibold text-card-foreground mb-1">{charity?.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{charity?.category} • {charity?.location}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBookmark}
                className="flex-shrink-0"
              >
                <Icon 
                  name={isBookmarked ? "Bookmark" : "BookmarkPlus"} 
                  size={16}
                  className={isBookmarked ? "text-primary" : "text-muted-foreground"}
                />
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{charity?.mission}</p>

            <div className="flex items-center justify-between">
              <TrustBadges />
              <div className="flex items-center space-x-2">
                <ImpactMetrics />
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleDonate}
                  iconName="Heart"
                  iconPosition="left"
                >
                  Donate
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-elevation-2 transition-gentle cursor-pointer group"
      onClick={handleCardClick}
    >
      {/* Header Image */}
      <div className="relative h-48 bg-muted overflow-hidden">
        <Image
          src={charity?.headerImage}
          alt={`${charity?.name} header`}
          className="w-full h-full object-cover group-hover:scale-105 transition-gentle"
        />
        <div className="absolute top-3 right-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBookmark}
            className="bg-background/80 backdrop-blur-sm hover:bg-background"
          >
            <Icon 
              name={isBookmarked ? "Bookmark" : "BookmarkPlus"} 
              size={16}
              className={isBookmarked ? "text-primary" : "text-muted-foreground"}
            />
          </Button>
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        {/* Logo and Title */}
        <div className="flex items-start space-x-3 mb-3">
          <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={charity?.logo}
              alt={`${charity?.name} logo`}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-card-foreground mb-1 line-clamp-1">{charity?.name}</h3>
            <p className="text-sm text-muted-foreground">{charity?.category}</p>
          </div>
        </div>

        {/* Mission */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{charity?.mission}</p>

        {/* Trust Badges */}
        <div className="mb-4">
          <TrustBadges />
        </div>

        {/* Impact Metrics */}
        <div className="mb-4">
          <ImpactMetrics />
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleDonate}
            iconName="Heart"
            iconPosition="left"
            className="flex-1"
          >
            Donate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e?.stopPropagation();
              navigate('/charity-profile', { state: { charity } });
            }}
          >
            View
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CharityCard;